import React from 'react'
import { Link } from 'react-router-dom'
import { Instagram, Facebook } from 'lucide-react'
const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear()

  const footerLinks = {
    coaching: [
      { name: 'The Strong Again Method™', href: '/private-training' },
      { name: 'Nutrition Blueprint', href: '/meal-plan' },
      { name: 'Physique & Competition Coaching', href: '/online-training' },
      { name: 'Complimentary Consultation', href: '/book-consultation' },
      { name: 'Manage My Consultation', href: '/manage-consultation' },
    
    ],
    resources: [
      {
        name: 'Which Starting-Over Story Is Yours?',
        href: '/starting-over-guide',
      },
      { name: 'Macro Calculator', href: '/macro-calculator' },
      { name: 'The First Step: A Free Reading', href: '/coming-home-to-me' },
      { name: 'FAQ', href: '/contact#faq' },
    ],    
    company: [
      { name: 'About Cassie', href: '/about' },
      { name: 'Contact', href: '/contact' },
      { name: 'Client Dashboard', href: '/dashboard' },
    ],
  }

const socialLinks = [
  {
    name: 'Instagram',
    icon: Instagram,
    href: 'https://www.instagram.com/fitbycassie_/',
  },
  {
    name: 'Facebook',
    icon: Facebook,
    href: 'https://www.facebook.com/share/1DkWAaoDAi/?mibextid=wwXIfr',
  },
]  
  return (
    <footer className="relative bg-brand-navy text-brand-cream overflow-hidden">
      {/* Subtle brand atmosphere */}
      <div
        className="absolute -top-32 -left-24 w-[420px] h-[420px] bg-brand-teal/10 rounded-full blur-3xl pointer-events-none"
        aria-hidden
      />

      {/* Main Footer Content */}
      <div className="relative container-max px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-5">
              <img
                src="/images/logo-light.webp"
                alt="Fit by Cassie"
                className="h-11 w-auto hover:opacity-85 transition-opacity"
              />
            </Link>

            <p className="text-xs text-brand-cream/50 uppercase tracking-widest-xl mb-4">
              Strong enough to keep saying yes to life.
            </p>

            <p className="text-brand-cream/70 mb-7 leading-relaxed max-w-sm text-sm">
              Personalized strength, nutrition, and physique coaching for women
              in their mid-30s and beyond—built around your body, your goals,
              and your actual life. Online-first coaching, wherever you train.
              Select in-person sessions are available in the DMV as an
              optional add-on for an additional fee, subject to availability.
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center text-brand-cream/65 hover:text-brand-blush transition-colors duration-300"
                  aria-label={social.name}
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Coaching */}
          <div>
            <h3 className="text-xs font-semibold mb-5 text-brand-ivory uppercase tracking-widest-xl">
              Coaching
            </h3>

            <ul className="space-y-3">
              {footerLinks.coaching.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-brand-cream/65 hover:text-brand-ivory transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-xs font-semibold mb-5 text-brand-ivory uppercase tracking-widest-xl">
              Resources
            </h3>

            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  {link.href.includes('#') ? (
                    <a
                      href={link.href}
                      className="text-brand-cream/65 hover:text-brand-ivory transition-colors text-sm"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-brand-cream/65 hover:text-brand-ivory transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-xs font-semibold mb-5 text-brand-ivory uppercase tracking-widest-xl">
              Company
            </h3>

            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-brand-cream/65 hover:text-brand-ivory transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Payment Options */}
        <div className="mt-12 pt-8 border-t border-brand-cream/10 text-center">
          <h3 className="text-xs font-semibold mb-3 text-brand-ivory uppercase tracking-widest-xl">
            Payment Options
          </h3>

          <p className="text-brand-cream/70 leading-relaxed text-sm max-w-xl mx-auto">
            Secure checkout is available through Stripe, including major
            credit and debit cards and eligible digital payment methods.
          </p>

          <p className="text-brand-cream/70 leading-relaxed text-sm max-w-xl mx-auto mt-2">
            Prefer Venmo or Zelle? Contact{' '}
            <a
              href="mailto:hello@fitbycassie.com"
              className="underline hover:text-brand-ivory transition-colors"
            >
              hello@fitbycassie.com
            </a>{' '}
            to discuss alternative payment arrangements before enrolling.
          </p>
        </div>

        {/* Credentials strip */}
        <div className="mt-12 pt-8 border-t border-brand-cream/10 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm text-brand-cream/50">
          {[
            'IFBB Bikini Pro',
            'Certified Fitness & Nutrition Coach',
            '20+ Years of Personal Fitness Experience'
          ].map((item) => (
            <span key={item} className="inline-flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-brand-bronze" />
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="relative border-t border-brand-cream/10">
        <div className="container-max">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 py-6 px-4 sm:px-6 lg:px-8">
            <p className="text-brand-cream/50 text-sm">
              © {currentYear} Fit by Cassie. All rights reserved.
            </p>

            <div className="flex items-center gap-6 text-sm text-brand-cream/50">
              <Link
                to="/privacy"
                className="hover:text-brand-ivory transition-colors"
              >
                Privacy Policy
              </Link>

              <Link
                to="/terms"
                className="hover:text-brand-ivory transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
